import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  TrendingUp, TrendingDown, Minus, AlertTriangle, Loader2, DollarSign,
  Leaf, Beef, Milk, Egg, Wheat, RefreshCw, Calculator, PiggyBank, Coins,
  Satellite, ExternalLink, CheckCircle2, Settings as SettingsIcon, ChevronRight
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import MarketCardModal from '../ui/MarketCardModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format a numeric value as USD with exactly two decimal places.
// Handles null/undefined/NaN safely and preserves the negative sign.
const formatUSD = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0.00';
  return n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Compact currency for tight card spaces. Shows readable abbreviated form
// on the 2×2 top-card grid; the tap-through modal always shows full precision.
//   $0.00 .. $999.99   → full ("$96.00", "$900.00")
//   $1,000 .. $9,999   → K form to 1 decimal ("$9.6K")
//   $10,000 .. $999K   → K form no decimal ("$99K", "$500K")
//   $1M+               → M form to 1 decimal ("$1.2M")
const compactUSD = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '$0.00';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs < 1000) return `${sign}$${formatUSD(abs)}`;
  if (abs < 10000) return `${sign}$${(abs / 1000).toFixed(1)}K`;
  if (abs < 1_000_000) return `${sign}$${Math.round(abs / 1000)}K`;
  return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
};

// Format a signed currency value with the minus sign positioned BEFORE the
// dollar prefix when caller renders "$" themselves. Returns "-X.XX" for
// negatives, "X.XX" for non-negatives (no leading "$"). When the value is
// negative, the consuming JSX should drop its hardcoded "$" prefix and use
// formatSignedUSD which includes the proper "-$" placement.
const formatSignedUSD = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '$0.00';
  const abs = Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${n < 0 ? '-' : ''}$${abs}`;
};

const categoryIcons = {
  garden_crops: Leaf,
  livestock: Beef,
  dairy: Milk,
  meat: Beef,
  eggs: Egg,
  cheese: Milk,
  feed: Wheat
};

const MarketInsights = () => {
  const { getAuthHeader, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [myPrices, setMyPrices] = useState(null);
  const [estimates, setEstimates] = useState(null);
  const [profitData, setProfitData] = useState(null);
  const [activeTab, setActiveTab] = useState('my-farm');
  
  // USDA Live Data state
  const [usdaCategory, setUsdaCategory] = useState('cattle');
  const [usdaData, setUsdaData] = useState(null);
  const [usdaLoading, setUsdaLoading] = useState(false);
  const [usdaReports, setUsdaReports] = useState([]);
  const [modalCard, setModalCard] = useState(null); // 'garden' | 'livestock' | 'dairy' | 'eggs' | null

  const fetchData = async (refresh = false) => {
    if (refresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const [pricesRes, estimatesRes, profitRes, reportsRes] = await Promise.all([
        axios.get(`${API_URL}/api/market/my-prices`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/market/estimate-value`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/market/profit-calculator`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/market/usda/reports`, { headers: getAuthHeader() })
      ]);
      
      setMyPrices(pricesRes.data);
      setEstimates(estimatesRes.data);
      setProfitData(profitRes.data);
      setUsdaReports(reportsRes.data.reports || []);
    } catch (error) {
      console.error('Failed to fetch market data:', error);
      toast.error('Failed to load market data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  const fetchUsdaData = async (category) => {
    setUsdaLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/market/usda/live-prices`, {
        headers: getAuthHeader(),
        params: { category }
      });
      setUsdaData(res.data);
    } catch (error) {
      console.error('Failed to fetch USDA data:', error);
      toast.error('Failed to fetch USDA prices');
    } finally {
      setUsdaLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);
  
  // Fetch USDA data when tab or category changes
  useEffect(() => {
    if (activeTab === 'usda-live' && user?.usda_api_key) {
      fetchUsdaData(usdaCategory);
    }
  }, [activeTab, usdaCategory]);

  const hasUserData = () => {
    if (!myPrices?.prices) return false;
    return Object.values(myPrices.prices).some(arr => arr.length > 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="market-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="market-insights">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
            Market Insights
          </h1>
          <p className="text-muted-foreground mt-1">
            Regional prices for your farm products
          </p>
        </div>
        <div className="flex items-center gap-2">
        <Button
          variant="outline"
          onClick={() => { window.location.href = '/app/reports?template=grant_application&include=market,inventory'; }}
          data-testid="export-to-report-market-btn"
          className="text-xs"
        >
          Export to Report
        </Button>
        <Button 
          variant="outline" 
          size="icon"
          onClick={() => fetchData(true)}
          disabled={refreshing}
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
        </Button>
        </div>
      </div>

      {/* Value Estimate Cards — 2x2 mobile, 4 across on desktop. Each card
          is tappable and opens a MarketCardModal with the full breakdown
          (crop-by-crop, herd-by-herd, projected/actual formulas, 30d history,
          pricing source). Cards themselves show a compact abbreviated value
          (e.g. "$9.6K") — modal always shows full precision. */}
      {estimates?.estimates && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <MarketTopCard
            testid="market-garden-card"
            onClick={() => setModalCard('garden')}
            gradient="from-green-50 to-emerald-50"
            borderClass="border-green-200"
            iconBg="bg-green-500/20"
            IconComponent={Leaf}
            iconColor="text-green-600"
            label="Garden Value"
            labelColor="text-green-700"
            valueColor="text-green-800"
            chevronColor="text-green-600/60"
            value={estimates.estimates.garden?.has_actual
              ? estimates.estimates.garden?.actual_total
              : estimates.estimates.garden?.total}
            valueTestId={estimates.estimates.garden?.has_actual ? 'garden-actual' : 'garden-projected'}
            valueColorOverride={estimates.estimates.garden?.has_actual ? 'text-emerald-700' : undefined}
            sub={estimates.estimates.garden?.has_actual
              ? `${estimates.estimates.garden?.actual_lbs} lbs actual`
              : `${estimates.estimates.garden?.items?.length || 0} crops projected`}
          />
          <MarketTopCard
            testid="market-livestock-card"
            onClick={() => setModalCard('livestock')}
            gradient="from-amber-50 to-orange-50"
            borderClass="border-amber-200"
            iconBg="bg-amber-500/20"
            IconComponent={Beef}
            iconColor="text-amber-600"
            label="Livestock Value"
            labelColor="text-amber-700"
            valueColor="text-amber-800"
            chevronColor="text-amber-600/60"
            value={estimates.estimates.livestock?.total}
            sub={`${estimates.estimates.livestock?.items?.length || 0} herds`}
          />
          <MarketTopCard
            testid="market-dairy-card"
            onClick={() => setModalCard('dairy')}
            gradient="from-blue-50 to-cyan-50"
            borderClass="border-blue-200"
            iconBg="bg-blue-500/20"
            IconComponent={Milk}
            iconColor="text-blue-600"
            label="Dairy (Monthly)"
            labelColor="text-blue-700"
            valueColor="text-blue-800"
            chevronColor="text-blue-600/60"
            value={estimates.estimates.dairy_monthly?.has_actual
              ? estimates.estimates.dairy_monthly?.actual_monthly
              : estimates.estimates.dairy_monthly?.total}
            valueTestId={estimates.estimates.dairy_monthly?.has_actual ? 'dairy-actual' : 'dairy-projected'}
            valueColorOverride={estimates.estimates.dairy_monthly?.has_actual ? 'text-emerald-700' : undefined}
            sub={estimates.estimates.dairy_monthly?.has_actual
              ? `${estimates.estimates.dairy_monthly?.actual_units} gal actual`
              : 'projected'}
          />
          <MarketTopCard
            testid="market-eggs-card"
            onClick={() => setModalCard('eggs')}
            gradient="from-yellow-50 to-amber-50"
            borderClass="border-yellow-200"
            iconBg="bg-yellow-500/20"
            IconComponent={Egg}
            iconColor="text-yellow-600"
            label="Eggs (Monthly)"
            labelColor="text-yellow-700"
            valueColor="text-yellow-800"
            chevronColor="text-yellow-600/60"
            value={estimates.estimates.eggs_monthly?.has_actual
              ? estimates.estimates.eggs_monthly?.actual_monthly
              : estimates.estimates.eggs_monthly?.total}
            valueTestId={estimates.estimates.eggs_monthly?.has_actual ? 'eggs-actual' : 'eggs-projected'}
            valueColorOverride={estimates.estimates.eggs_monthly?.has_actual ? 'text-emerald-700' : undefined}
            sub={estimates.estimates.eggs_monthly?.has_actual
              ? `${estimates.estimates.eggs_monthly?.actual_units} eggs actual`
              : 'projected'}
          />
        </div>
      )}

      {/* Modal detail overlay */}
      <MarketCardModal
        open={!!modalCard}
        onOpenChange={(v) => setModalCard(v ? modalCard : null)}
        cardType={modalCard}
        data={
          modalCard === 'garden'    ? estimates?.estimates?.garden :
          modalCard === 'livestock' ? estimates?.estimates?.livestock :
          modalCard === 'dairy'     ? estimates?.estimates?.dairy_monthly :
          modalCard === 'eggs'      ? estimates?.estimates?.eggs_monthly : null
        }
      />

      {/* Tabs for different price views */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 h-auto">
          <TabsTrigger value="my-farm" data-testid="my-farm-tab" className="text-xs sm:text-sm px-1 sm:px-3">
            <span className="hidden sm:inline">My Farm</span>
            <span className="sm:hidden">Farm</span>
          </TabsTrigger>
          <TabsTrigger value="usda-live" data-testid="usda-live-tab" className="flex items-center gap-1 text-xs sm:text-sm px-1 sm:px-3">
            <Satellite className="w-3 h-3" />
            <span className="hidden sm:inline">USDA</span>
            <span className="sm:hidden">Live</span>
          </TabsTrigger>
          <TabsTrigger value="profit" data-testid="profit-tab" className="text-xs sm:text-sm px-1 sm:px-3">
            <Calculator className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
            <span className="hidden sm:inline">Profit</span>
          </TabsTrigger>
          <TabsTrigger value="detailed" data-testid="detailed-tab" className="text-xs sm:text-sm px-1 sm:px-3">
            <span className="hidden sm:inline">Breakdown</span>
            <span className="sm:hidden">Detail</span>
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="my-farm" className="mt-4">
          {/* Show USDA connection prompt if no API key */}
          {!user?.usda_api_key && (
            <Card className="card-farm border-amber-200 bg-amber-50/30 mb-4">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-900">Live market prices unavailable</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Connect your USDA Market News API key in Settings to see real-time prices for your livestock and crops.
                    </p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-2 border-amber-300 text-amber-700 hover:bg-amber-100"
                      onClick={() => window.location.href = '/app/settings'}
                    >
                      <SettingsIcon className="w-4 h-4 mr-2" />
                      Connect USDA API
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          {!hasUserData() ? (
            <Card className="card-farm">
              <CardContent className="py-12 text-center">
                <DollarSign className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-medium text-lg mb-2">No Farm Data Yet</h3>
                <p className="text-muted-foreground mb-4">
                  Add crops to your Garden Planner or livestock to see relevant market prices.
                </p>
                <div className="flex gap-3 justify-center">
                  <Button variant="outline" onClick={() => window.location.href = '/app/garden'}>
                    <Leaf className="w-4 h-4 mr-2" />
                    Garden Planner
                  </Button>
                  <Button variant="outline" onClick={() => window.location.href = '/app/livestock'}>
                    <Beef className="w-4 h-4 mr-2" />
                    Livestock
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {/* Garden Crops */}
              {myPrices?.prices?.garden_crops?.length > 0 && (
                <Card className="card-farm">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <Leaf className="w-5 h-5 text-green-600" />
                      Your Garden Crops
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {myPrices.prices.garden_crops.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-green-50/50 rounded-lg">
                          <span className="font-medium">{item.crop}</span>
                          <span className="text-green-700 font-mono">${item.price?.toFixed(2)}/{item.unit?.replace('$/', '')}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Livestock */}
              {myPrices?.prices?.livestock?.length > 0 && (
                <Card className="card-farm">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <Beef className="w-5 h-5 text-amber-600" />
                      Live Animal Prices
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {myPrices.prices.livestock.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-amber-50/50 rounded-lg">
                          <span className="font-medium">{item.name}</span>
                          <span className="text-amber-700 font-mono">{item.unit}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Meat Prices */}
              {myPrices?.prices?.meat?.length > 0 && (
                <Card className="card-farm">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <Beef className="w-5 h-5 text-red-600" />
                      Meat Prices
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {myPrices.prices.meat.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-red-50/50 rounded-lg">
                          <span className="font-medium">{item.name}</span>
                          <span className="text-red-700 font-mono">{item.unit}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Dairy */}
              {myPrices?.prices?.dairy?.length > 0 && (
                <Card className="card-farm">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <Milk className="w-5 h-5 text-blue-600" />
                      Dairy Prices
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {myPrices.prices.dairy.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-blue-50/50 rounded-lg">
                          <span className="font-medium">{item.name}</span>
                          <span className="text-blue-700 font-mono">{item.unit}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Cheese */}
              {myPrices?.prices?.cheese?.length > 0 && (
                <Card className="card-farm">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <Milk className="w-5 h-5 text-yellow-600" />
                      Cheese Prices
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {myPrices.prices.cheese.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-yellow-50/50 rounded-lg">
                          <span className="font-medium">{item.name}</span>
                          <span className="text-yellow-700 font-mono">{item.unit}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Eggs */}
              {myPrices?.prices?.eggs?.length > 0 && (
                <Card className="card-farm">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <Egg className="w-5 h-5 text-yellow-500" />
                      Egg Prices
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {myPrices.prices.eggs.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-yellow-50/30 rounded-lg">
                          <span className="font-medium">{item.name}</span>
                          <span className="text-yellow-700 font-mono">{item.unit}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Feed Costs */}
              {myPrices?.prices?.feed?.length > 0 && (
                <Card className="card-farm">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <Wheat className="w-5 h-5 text-amber-700" />
                      Feed Costs
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {myPrices.prices.feed.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-amber-50/30 rounded-lg">
                          <span className="font-medium">{item.name}</span>
                          <span className="text-amber-800 font-mono">{item.unit}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="usda-live" className="mt-4">
          {!user?.usda_api_key ? (
            <Card className="card-farm">
              <CardContent className="py-12 text-center">
                <Satellite className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-medium text-lg mb-2">Connect USDA Market News</h3>
                <p className="text-muted-foreground mb-4">
                  Add your free USDA Market News API key to see live commodity prices.
                </p>
                <Button variant="outline" onClick={() => window.location.href = '/app/settings'}>
                  <SettingsIcon className="w-4 h-4 mr-2" />
                  Add API Key in Settings
                </Button>
                <div className="mt-4 text-xs text-muted-foreground">
                  <a 
                    href="https://mymarketnews.ams.usda.gov" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline flex items-center justify-center gap-1"
                  >
                    Get your free API key from USDA <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {/* Category Selector */}
              <Card className="card-farm">
                <CardContent className="py-4">
                  <div className="flex flex-wrap gap-2">
                    {usdaReports.map((report) => (
                      <Button
                        key={report.id}
                        variant={usdaCategory === report.id ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setUsdaCategory(report.id)}
                        className={usdaCategory === report.id ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                        data-testid={`usda-category-${report.id}`}
                      >
                        {report.name}
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>
              
              {/* USDA Live Prices */}
              {usdaLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : usdaData?.error ? (
                <Card className="card-farm border-amber-200">
                  <CardContent className="py-8 text-center">
                    <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
                    <h3 className="font-medium text-lg mb-2">{usdaData.error}</h3>
                    <p className="text-muted-foreground text-sm">{usdaData.message}</p>
                    {usdaData.error.includes('Invalid') && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="mt-4"
                        onClick={() => window.location.href = '/app/settings'}
                      >
                        Check API Key
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ) : (usdaData?.prices?.length > 0 || usdaData?.narratives?.length > 0) ? (
                <Card className="card-farm">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="font-heading text-lg flex items-center gap-2">
                        <Satellite className="w-5 h-5 text-emerald-600" />
                        Live USDA Prices: {usdaReports.find(r => r.id === usdaCategory)?.name}
                      </CardTitle>
                      <div className="flex items-center gap-1 text-xs text-emerald-600">
                        <CheckCircle2 className="w-3 h-3" />
                        Live Data
                      </div>
                    </div>
                    {usdaData.report_title && (
                      <p className="text-xs text-muted-foreground mt-1" data-testid="usda-report-title">
                        {usdaData.report_title}
                      </p>
                    )}
                  </CardHeader>
                  <CardContent>
                    {usdaData.prices?.length > 0 && (
                      <div className="overflow-x-auto" data-testid="usda-price-table">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left py-2 px-2">Commodity</th>
                              <th className="text-left py-2 px-2 hidden sm:table-cell">Grade</th>
                              <th className="text-right py-2 px-2">Low</th>
                              <th className="text-right py-2 px-2">High</th>
                              <th className="text-right py-2 px-2 font-bold">Avg</th>
                            </tr>
                          </thead>
                          <tbody>
                            {usdaData.prices.map((item, idx) => (
                              <tr key={idx} className="border-b border-muted hover:bg-muted/30">
                                <td className="py-2 px-2 font-medium">{item.commodity}</td>
                                <td className="py-2 px-2 text-muted-foreground hidden sm:table-cell">{item.grade || '-'}</td>
                                <td className="py-2 px-2 text-right font-mono text-sm">
                                  {item.price_low ? `$${item.price_low.toFixed(2)}` : '-'}
                                </td>
                                <td className="py-2 px-2 text-right font-mono text-sm">
                                  {item.price_high ? `$${item.price_high.toFixed(2)}` : '-'}
                                </td>
                                <td className="py-2 px-2 text-right font-mono font-bold text-emerald-700">
                                  {item.price_avg ? `$${item.price_avg.toFixed(2)}` : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {usdaData.narratives?.length > 0 && (
                      <div className="mt-4 space-y-3" data-testid="usda-narratives">
                        <h4 className="text-sm font-semibold text-foreground">Market Report</h4>
                        {usdaData.narratives.map((n, idx) => (
                          <p key={idx} className="text-xs text-muted-foreground whitespace-pre-wrap leading-relaxed border-l-2 border-emerald-200 pl-3">
                            {n}
                          </p>
                        ))}
                      </div>
                    )}
                    <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                      <span>Source: {usdaData.source}</span>
                      <span>Updated: {new Date(usdaData.updated_at).toLocaleString()}</span>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="card-farm">
                  <CardContent className="py-8 text-center">
                    <p className="text-muted-foreground">No price data available for this category. Try selecting a different commodity type.</p>
                  </CardContent>
                </Card>
              )}
              
              <p className="text-xs text-muted-foreground text-center">
                Data provided by USDA Agricultural Marketing Service. Prices are for informational purposes only.
              </p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="profit" className="mt-4">
          {profitData ? (
            <div className="space-y-4">
              {/* Profit Summary Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="card-farm bg-gradient-to-br from-green-50 to-emerald-50 border-green-300">
                  <CardContent className="py-4">
                    <div className="flex items-center gap-2 mb-2">
                      <PiggyBank className="w-5 h-5 text-green-600" />
                      <span className="text-sm text-green-700">Monthly Revenue</span>
                    </div>
                    <p className="font-heading text-2xl font-bold text-green-800">
                      {formatSignedUSD(profitData.totals?.monthly_revenue)}
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="card-farm bg-gradient-to-br from-red-50 to-orange-50 border-red-200">
                  <CardContent className="py-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Wheat className="w-5 h-5 text-red-600" />
                      <span className="text-sm text-red-700">Monthly Feed Cost</span>
                    </div>
                    <p className="font-heading text-2xl font-bold text-red-800">
                      -${formatUSD(profitData.totals?.monthly_feed_cost)}
                    </p>
                  </CardContent>
                </Card>
                
                <Card className={`card-farm ${(profitData.totals?.net_monthly_profit || 0) >= 0 ? 'bg-gradient-to-br from-emerald-50 to-green-100 border-emerald-300' : 'bg-gradient-to-br from-red-50 to-red-100 border-red-300'}`}>
                  <CardContent className="py-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Coins className="w-5 h-5 text-emerald-600" />
                      <span className="text-sm text-emerald-700">Net Monthly</span>
                    </div>
                    <p className={`font-heading text-2xl font-bold ${(profitData.totals?.net_monthly_profit || 0) >= 0 ? 'text-emerald-800' : 'text-red-800'}`}>
                      {formatSignedUSD(profitData.totals?.net_monthly_profit)}
                    </p>
                  </CardContent>
                </Card>
                
                <Card className={`card-farm ${(profitData.totals?.net_annual_profit || 0) >= 0 ? 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-300' : 'bg-gradient-to-br from-red-50 to-red-100 border-red-300'}`}>
                  <CardContent className="py-4">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-5 h-5 text-blue-600" />
                      <span className="text-sm text-blue-700">Net Annual</span>
                    </div>
                    <p className={`font-heading text-2xl font-bold ${(profitData.totals?.net_annual_profit || 0) >= 0 ? 'text-blue-800' : 'text-red-800'}`}>
                      {formatSignedUSD(profitData.totals?.net_annual_profit)}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Egg Production */}
              {profitData.production?.eggs?.items?.length > 0 && (
                <Card className="card-farm">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <Egg className="w-5 h-5 text-yellow-500" />
                      Egg Production
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {profitData.production.eggs.items.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-yellow-50/50 rounded-lg">
                          <div>
                            <p className="font-medium">{item.name}</p>
                            <p className="text-sm text-muted-foreground">{item.birds} birds</p>
                          </div>
                          <div className="text-right">
                            <p className="font-mono">{item.eggs_per_day} eggs/day</p>
                          </div>
                        </div>
                      ))}
                      <div className="flex justify-between p-3 bg-yellow-100 rounded-lg font-medium">
                        <span>Monthly Revenue</span>
                        <span className="text-yellow-800">${formatUSD(profitData.production.eggs.monthly_revenue)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Milk Production — card renders when EITHER livestock herds have
                  dairy purpose OR the user has logged any milk records via
                  foundation stock (per Emergent support diagnosis, Step 5 redux). */}
              {(profitData.production?.milk?.items?.length > 0 || profitData.production?.milk?.actual_record_count > 0) && (
                <Card className="card-farm">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <Milk className="w-5 h-5 text-blue-500" />
                      Milk Production
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {(profitData.production.milk.items || []).map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-blue-50/50 rounded-lg">
                          <div>
                            <p className="font-medium">{item.name}</p>
                            <p className="text-sm text-muted-foreground">{item.count} {item.type}s</p>
                          </div>
                          <div className="text-right">
                            <p className="font-mono">{item.gallons_per_day} gal/day</p>
                          </div>
                        </div>
                      ))}
                      {/* Monthly revenue only meaningful when livestock herds exist
                          (foundation-only users still see actual rollup below) */}
                      {(profitData.production.milk.items?.length > 0) && (
                        <div className="flex justify-between p-3 bg-blue-100 rounded-lg font-medium">
                          <span>Monthly Revenue (10-mo lactation)</span>
                          <span className="text-blue-800">${formatUSD(profitData.production.milk.monthly_revenue)}</span>
                        </div>
                      )}
                      {/* Step 5: actual logged production rollup — always shown so
                          users can see their tracking effort reflected (or learn that
                          their logs are dated outside the rollup window). */}
                      <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="dairy-actual-rollup">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-emerald-800">Actually logged</p>
                          <p className="font-mono text-emerald-900">{profitData.production.milk.actual_total_gallons ?? 0} gal</p>
                        </div>
                        <div className="mt-1 flex items-center justify-between text-xs text-emerald-700">
                          <span>
                            {profitData.production.milk.actual_record_count > 0 ? (
                              <>
                                {profitData.production.milk.actual_record_count} record{profitData.production.milk.actual_record_count === 1 ? '' : 's'}
                                {' • '}{profitData.production.milk.actual_per_animal_count > 0 && `${profitData.production.milk.actual_per_animal_count} per-animal`}
                                {profitData.production.milk.actual_per_animal_count > 0 && profitData.production.milk.actual_herd_level_count > 0 && ', '}
                                {profitData.production.milk.actual_herd_level_count > 0 && `${profitData.production.milk.actual_herd_level_count} herd-level`}
                              </>
                            ) : (
                              <>No logs in the last {profitData.production.milk.actual_lookback_days ?? 365} days. Log milk on a herd or animal to start tracking.</>
                            )}
                          </span>
                          {profitData.production.milk.actual_most_recent_date && (
                            <span className="font-mono">Last: {profitData.production.milk.actual_most_recent_date}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Cheese Production */}
              {profitData.production?.cheese?.monthly_total > 0 && (
                <Card className="card-farm">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <Milk className="w-5 h-5 text-amber-500" />
                      Cheese Production (Est.)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="p-3 bg-amber-50/50 rounded-lg">
                      <p className="text-sm text-muted-foreground mb-2">
                        Assuming 50% of milk is used for cheese making
                      </p>
                      <div className="flex justify-between">
                        <span>~{profitData.production.cheese.monthly_total} lbs/month</span>
                        <span className="font-mono text-amber-800">${formatUSD(profitData.production.cheese.monthly_revenue)}/mo</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Meat Production */}
              {profitData.production?.meat?.items?.length > 0 && (
                <Card className="card-farm">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <Beef className="w-5 h-5 text-red-500" />
                      Meat Sales (Annual)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {profitData.production.meat.items.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-red-50/50 rounded-lg">
                          <div>
                            <p className="font-medium">{item.name}</p>
                            <p className="text-sm text-muted-foreground">{item.annual_sales} {item.type}s sold/year</p>
                          </div>
                          <span className="font-mono text-red-800">${formatUSD(item.revenue)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between p-3 bg-red-100 rounded-lg font-medium">
                        <span>Annual Meat Revenue</span>
                        <span className="text-red-800">${formatUSD(profitData.production.meat.annual_revenue)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Garden Revenue */}
              {profitData.production?.garden?.items?.[0]?.total_plants > 0 && (
                <Card className="card-farm">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <Leaf className="w-5 h-5 text-green-500" />
                      Garden Revenue (Seasonal)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="p-3 bg-green-50/50 rounded-lg">
                      <div className="flex justify-between">
                        <span>{profitData.production.garden.items[0].total_plants} plants @ ${formatUSD(profitData.production.garden.items[0].avg_value_per_plant)}/plant</span>
                        <span className="font-mono text-green-800">${formatUSD(profitData.production.garden.seasonal_revenue)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Feed Costs Breakdown */}
              {profitData.feed_costs?.items?.length > 0 && (
                <Card className="card-farm border-red-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <Wheat className="w-5 h-5 text-red-500" />
                      Feed Costs
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {profitData.feed_costs.items.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-red-50/30 rounded">
                          <span className="text-sm">{item.animal}</span>
                          <span className="font-mono text-sm text-red-700">${formatUSD(item.daily_cost)}/day</span>
                        </div>
                      ))}
                      <div className="flex justify-between p-3 bg-red-100 rounded-lg font-medium mt-2">
                        <span>Total Monthly Feed</span>
                        <span className="text-red-800">-${formatUSD(profitData.feed_costs.monthly)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              <p className="text-xs text-muted-foreground text-center p-4 bg-muted/30 rounded-lg">
                {profitData.disclaimer}
              </p>
            </div>
          ) : (
            <Card className="card-farm">
              <CardContent className="py-12 text-center">
                <Calculator className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-medium text-lg mb-2">Add Livestock to Calculate Profit</h3>
                <p className="text-muted-foreground mb-4">
                  The profit calculator requires livestock data to estimate production and revenue.
                </p>
                <Button variant="outline" onClick={() => window.location.href = '/app/livestock'}>
                  <Beef className="w-4 h-4 mr-2" />
                  Go to Livestock
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        <TabsContent value="detailed" className="mt-4">
          <div className="space-y-4">
            {/* Garden Crop Value Breakdown */}
            {estimates?.estimates?.garden?.items?.length > 0 && (
              <Card className="card-farm">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Leaf className="w-5 h-5 text-green-600" />
                    Garden Harvest Estimate
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2">Crop</th>
                          <th className="text-right py-2">Plants</th>
                          <th className="text-right py-2">Est. Yield</th>
                          <th className="text-right py-2">Price/lb</th>
                          <th className="text-right py-2">Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {estimates.estimates.garden.items.map((item, idx) => (
                          <tr key={idx} className="border-b border-muted">
                            <td className="py-2 font-medium">{item.crop}</td>
                            <td className="py-2 text-right">{item.plants}</td>
                            <td className="py-2 text-right">{item.est_yield_lbs} lbs</td>
                            <td className="py-2 text-right">${item.price_per_lb.toFixed(2)}</td>
                            <td className="py-2 text-right font-mono text-green-700">${item.est_value.toFixed(2)}</td>
                          </tr>
                        ))}
                        <tr className="font-bold">
                          <td colSpan="4" className="py-2 text-right">Total:</td>
                          <td className="py-2 text-right text-green-700">${estimates.estimates.garden.total.toFixed(2)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-muted-foreground mt-3">
                    * Estimates assume average yield of 5 lbs per plant. Actual yields vary by variety and growing conditions.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Livestock Value Breakdown */}
            {estimates?.estimates?.livestock?.items?.length > 0 && (
              <Card className="card-farm">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Beef className="w-5 h-5 text-amber-600" />
                    Livestock Value Estimate
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {estimates.estimates.livestock.items.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-amber-50/50 rounded-lg">
                        <div>
                          <p className="font-medium">{item.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {item.count} head • {item.type}
                          </p>
                        </div>
                        <span className="font-mono text-amber-700 font-bold">
                          ${formatUSD(item.est_value)}
                        </span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between p-3 bg-amber-100 rounded-lg font-bold">
                      <span>Total Livestock Value</span>
                      <span className="text-amber-800">${formatUSD(estimates.estimates.livestock.total)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Disclaimer - moved to bottom */}
      <Card className="card-farm bg-amber-50/50 border-amber-200">
        <CardContent className="py-3">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-amber-800 mb-1">Regional Price Estimates</p>
              <p className="text-amber-700 text-xs">
                Prices shown are regional averages and may vary by location, season, and market conditions. 
                For official prices, consult your local market or USDA reports.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground text-center">
        Last updated: {myPrices?.updated_at ? new Date(myPrices.updated_at).toLocaleString() : 'N/A'}
      </p>
    </div>
  );
};

export default MarketInsights;

/**
 * MarketTopCard — one of the four tappable summary cards at the top of
 * Market Insights. Shows a COMPACT dollar amount (e.g. "$9.6K") so it fits
 * cleanly in a 2×2 mobile grid without any truncation, font-shrinking, or
 * layout gymnastics. The tap-through MarketCardModal shows full precision
 * and methodology.
 */
function MarketTopCard({
  testid, onClick, gradient, borderClass, iconBg, IconComponent, iconColor,
  label, labelColor, valueColor, chevronColor, value, sub, valueTestId, valueColorOverride,
}) {
  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      data-testid={testid}
      className={`card-farm relative bg-gradient-to-br ${gradient} ${borderClass} cursor-pointer transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary`}
    >
      <CardContent className="py-4 px-4">
        <div className="flex items-center gap-2 mb-2">
          <div className={`w-8 h-8 rounded-full ${iconBg} flex items-center justify-center flex-shrink-0`}>
            <IconComponent className={`w-4 h-4 ${iconColor}`} />
          </div>
          <span className={`text-sm ${labelColor}`}>{label}</span>
        </div>
        <p
          className={`font-heading text-2xl font-bold tabular-nums ${valueColorOverride || valueColor}`}
          data-testid={valueTestId}
        >
          {compactUSD(value)}
        </p>
        <p className={`text-xs ${labelColor} opacity-80`}>{sub}</p>
        <ChevronRight
          className={`absolute bottom-2 right-2 w-4 h-4 ${chevronColor}`}
          aria-hidden="true"
        />
      </CardContent>
    </Card>
  );
}
